import React, { useState,useEffect } from "react";
import { Plus } from "lucide-react";
import Button from "../components/shared/Button";
import { SidebarItem } from "./shared/SidebarItem";
import { mainNavItems, otherNavItems } from "../config/sidebarItems";
import { useNavigate, useLocation } from "react-router-dom";
import CreateDocumentModal from '../components/modals/createDocumentModal'; 
import ConfirmationModal from "../components/modals/confirmationModal";
import logoutModalIcon from "../assets/icons/logout-modal-icon.svg";
import { useAuthContext } from "../components/AuthProvider";
import EditProfileModal from "./modals/editProfileModal";
import ChangePasswordModal from "./modals/changePasswordModal";
import Modal from "./modals/modal";
import profileUpdatedModalIcon from "../assets/icons/profile-updated-modal-icon.svg";
import { Toast } from "./shared/Toast";
import { UserService } from "../services";
import UploadPhotoModal from "./modals/uploadPhotoModal";
import Tooltip from "./shared/Tooltip";

export default function Sidebar() {
  	const getEmailFromToken = () => {
		try {
			const token = localStorage.getItem('authToken');
			if (!token) return '';
			const parts = token.split('.');
			if (parts.length !== 3) return '';
			const base64Url = parts[1];
			const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
			const jsonPayload = decodeURIComponent(
				atob(base64)
					.split('')
					.map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
					.join('')
			);
			const payload = JSON.parse(jsonPayload);
			return payload.email || payload.username || payload.sub || '';
		} catch (e) {
			return '';
		}
	};
  const [activeItem, setActiveItem] = useState("dashboard");
  const [isCreateDocumentModalOpen, setIsCreateDocumentModalOpen] = useState(false); 
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  const [isEditProfileModalOpen, setIsEditProfileModalOpen] = useState(false);
  const [isProfileUpdatedModalOpen, setIsProfileUpdatedModalOpen] = useState(false);
  const [passwordChangeSuccessMessage, setPasswordChangeSuccessMessage] = useState(''); 

  const navigate = useNavigate();
  const location = useLocation();
    // Get user data from authentication context
  const { user, getUserPicture, updateUserData, logout } = useAuthContext();
  
    // Initialize user profile with data from auth context
    const [userProfile, setUserProfile] = useState({
      firstName: user?.firstName || "",
      lastName: user?.lastName || "",
      email: user?.email || getEmailFromToken() || "",
      photo: getUserPicture(),
    });
  
    // Update user profile when user data changes (only when user object changes)
    useEffect(() => {
    if (user) {
      setUserProfile({
      firstName: user.firstName || "",
      lastName: user.lastName || "",
      email: user.email || getEmailFromToken() || "",
      photo: user.pictureUrl || getUserPicture(),
      });
    }
    }, [user, getUserPicture]);
  
    const handleUploadDrive = () => setIsUploadModalOpen(false);
    const handleUploadDevice = () => setIsUploadModalOpen(false);
    const closeProfileUpdatedOrPasswordChangedModal = () => {
      setIsProfileUpdatedModalOpen(false);
      if (passwordChangeSuccessMessage) {
        logout();
        navigate('/login', { replace: true });
      }
    };
  
    // Updated handler for password change success
    const handleChangePassword = (message) => {
      setIsChangePasswordOpen(false); // Close the change password modal
      setPasswordChangeSuccessMessage(message); // Set the custom success message
      setIsProfileUpdatedModalOpen(true); // Open the profile updated modal
    };
  
    const handleSaveProfile = async (updatedProfile) => {
      try {
        // Prepare profile data for API - only include fields that have values
        const profileData = {};
        
        // Only add fields that have values and are different from current user data
        if (updatedProfile.firstName && updatedProfile.firstName.trim() !== '') {
          profileData.firstName = updatedProfile.firstName.trim();
        }
        if (updatedProfile.lastName && updatedProfile.lastName.trim() !== '') {
          profileData.lastName = updatedProfile.lastName.trim();
        }
        if (updatedProfile.profileImage) {
          profileData.profileImage = updatedProfile.profileImage;
        }
  
        // Check if at least one field is provided
        if (Object.keys(profileData).length === 0) {
          Toast.error('Please provide at least one field to update');
          return;
        }
  
        // Call the update profile API
        const response = await UserService.updateProfile(profileData);
  
        if (response.status === 200 && response.customer) {
          // Update auth context with new customer data
          updateUserData(response.customer);
          
          // Show success toast
          // Toast.success(response.message || 'Profile updated successfully');
  
          // Close modal and show confirmation
          setIsEditProfileModalOpen(false);
          setIsProfileUpdatedModalOpen(true);
        } else {
          // Handle error
          const errorMessage = response.message || 'Failed to update profile';
          Toast.error(errorMessage);
        }
      } catch (error) {
        Toast.error('Network error. Please try again.');
      }
    };

    const handleSidebarItemClick = (item) => {
      // 1. Handle Modal Clicks (Child Items)
      if (item.id === "profile") {
        setIsEditProfileModalOpen(true);
      } else if (item.id === "security") {
        setIsChangePasswordOpen(true);
      } 
      // 2. Handle Logout Modal
      else if (item.id === "logout") {
        setIsLogoutModalOpen(true);
      } 
      // 3. Handle Standard Navigation (Parent Items and others)
      else if (item.path) {
        setActiveItem(item.id);
        navigate(item.path);
      }
    };

  // Keep active item in sync with current URL (including on refresh)
  useEffect(() => {
    const allItems = [...mainNavItems, ...otherNavItems];
    const path = location.pathname || '';
    // Find best match: exact path or startsWith for nested routes
    const match = allItems.find((it) => it.path && (path === it.path || path.startsWith(it.path + '/')));
    if (match && match.id !== activeItem) {
      setActiveItem(match.id);
    }
  }, [location.pathname,activeItem]);

  const handleLogout = () => {
    setIsLogoutModalOpen(false); // close modal
    logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="bg-white lg:max-w-[273px] max-w-[240px] hidden lg:block">
      <div className="pt-10">
        {/* New Document Button */}
        <div className="px-6 mb-10">
          <Tooltip content="Start a new paper, thesis, or dissertation with APA style applied.">
            <Button className="w-full" onClick={() => setIsCreateDocumentModalOpen(true)}>
              <div className="flex items-center justify-center gap-2">
                <Plus className="w-4 h-4 group-hover:text-orange transition-colors duration-200" />
                <span className="group-hover:text-hover-gradient">New Document</span>
              </div>
            </Button>
          </Tooltip>
        </div>

        {/* Main Navigation */}
        <div className="px-6 mb-10">
          <div className="mb-5">
            <Tooltip content="Create and manage your documents.">
              <div className="inline-block relative">
                <h3 className="text-xs font-semibold text-dark-blue opacity-50 uppercase tracking-wider">MENU</h3>
              </div>
            </Tooltip>
          </div>
          <div className="space-y-2.5">
            {mainNavItems.map((item) => (
              <SidebarItem
                key={item.id}
                item={item} 
                activeItem={activeItem}
                onClick={handleSidebarItemClick}
              />
            ))}
          </div>
        </div>

        {/* Divider */}
        <div className="mx-6 mb-10">
          <div className="h-px bg-gray-100"></div>
        </div>

        {/* Other Navigation */}
        <div className="px-6">
          <div className="mb-5">
            <Tooltip content="Manage your account, settings, and support.">
              <div className="inline-block relative">
                <h3 className="text-xs font-semibold text-dark-blue opacity-50 uppercase tracking-wider">ACCOUNT</h3>
              </div>
            </Tooltip>
          </div>
          <div className="space-y-2.5">
            {otherNavItems.map((item) => (
              <SidebarItem
                key={item.id}
                item={item}
                activeItem={activeItem}
                onClick={handleSidebarItemClick}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Create Document Modal */}
      <CreateDocumentModal
        isOpen={isCreateDocumentModalOpen}
        onClose={() => setIsCreateDocumentModalOpen(false)}
        isImported={false}
      />

      {/* Logout Confirmation Modal */}
      <ConfirmationModal
          isOpen={isLogoutModalOpen}
          onClose={() => setIsLogoutModalOpen(false)}
          icon={<img src={logoutModalIcon} alt="Logout" className="mx-auto" />}
          heading={<em>Are you sure you want to log out?</em>}
          description={<em>Don't worry — your work is saved, and you can log back in anytime.</em>}
          primaryButtonText="Cancel"
          secondaryButtonText="Logout"
          onPrimaryClick={() => setIsLogoutModalOpen(false)}
          onSecondaryClick={handleLogout}
      />
            {/* Upload Photo Modal */}
        <UploadPhotoModal
          isOpen={isUploadModalOpen}
          onClose={() => setIsUploadModalOpen(false)}
          onUploadDrive={handleUploadDrive}
          onUploadDevice={handleUploadDevice}
        />

        {/* Change Password Modal */}
        <ChangePasswordModal
          isOpen={isChangePasswordOpen}
          onClose={() => setIsChangePasswordOpen(false)}
          onChangePassword={handleChangePassword}
        />

        {/* Edit Profile Modal */}
        <EditProfileModal
          isOpen={isEditProfileModalOpen}
          onClose={() => setIsEditProfileModalOpen(false)}
          userProfile={userProfile}
          onSaveProfile={handleSaveProfile}
        />

        {/* Profile Updated Confirmation Modal */}
        <Modal
          isOpen={isProfileUpdatedModalOpen}
          onClose={() => {
            setIsProfileUpdatedModalOpen(false);
            setPasswordChangeSuccessMessage(''); // Clear message on close
          }}
          icon={<img src={profileUpdatedModalIcon} alt="Profile Updated" className="mx-auto" />}
          heading="Success!"
          description={
            passwordChangeSuccessMessage ||
            ' Your account profile has been updated.'
          }
          buttonText="Close"
          onButtonClick={() => closeProfileUpdatedOrPasswordChangedModal()}
        />

    </div>
  );
}